import type {
  AutoDormCheckGrantInput,
  AutoDormCheckGrantScope,
  AutoDormCheckPlanCode,
  AutoDormCheckQuotaKind,
} from "./types";

export const AUTO_DORM_CHECK_QUOTA_LIMITS = {
  time: { min: 1, max: 3650 },
  count: { min: 1, max: 100000 },
} as const;

export interface AutoDormCheckGrantDraft {
  quotaKind: AutoDormCheckQuotaKind;
  quotaAmount: string;
  scope: AutoDormCheckGrantScope;
  selectedUserIds: string[];
  paidPlanCode: AutoDormCheckPlanCode | null;
  includeFuture: boolean;
}

export function createAutoDormCheckGrantDraft(): AutoDormCheckGrantDraft {
  return {
    quotaKind: "time",
    quotaAmount: "",
    scope: "selected_users",
    selectedUserIds: [],
    paidPlanCode: null,
    includeFuture: false,
  };
}

export function parseAutoDormCheckQuotaAmount(
  value: string,
  kind: AutoDormCheckQuotaKind,
): number | null {
  const normalized = value.trim();
  if (!/^\d+$/.test(normalized)) return null;
  const amount = Number(normalized);
  const limit = AUTO_DORM_CHECK_QUOTA_LIMITS[kind];
  if (
    !Number.isSafeInteger(amount) ||
    amount < limit.min ||
    amount > limit.max
  ) {
    return null;
  }
  return amount;
}

export function validateAutoDormCheckGrantDraft(
  draft: AutoDormCheckGrantDraft,
): string | null {
  if (
    parseAutoDormCheckQuotaAmount(draft.quotaAmount, draft.quotaKind) === null
  ) {
    return draft.quotaKind === "time"
      ? "请输入 1–3650 之间的整数天数"
      : "请输入 1–100000 之间的整数次数";
  }
  const selectedUserCount = new Set(
    draft.selectedUserIds.map((id) => id.trim()).filter(Boolean),
  ).size;
  if (draft.scope === "selected_users" && selectedUserCount === 0) {
    return "请至少选择一位用户";
  }
  if (draft.scope === "selected_users" && selectedUserCount > 500) {
    return "一次最多选择 500 位用户";
  }
  if (draft.scope === "paid_plan" && !draft.paidPlanCode) {
    return "请选择用于筛选付费用户的套餐";
  }
  return null;
}

export function buildAutoDormCheckGrantInput(
  draft: AutoDormCheckGrantDraft,
): AutoDormCheckGrantInput | null {
  const quotaAmount = parseAutoDormCheckQuotaAmount(
    draft.quotaAmount,
    draft.quotaKind,
  );
  if (validateAutoDormCheckGrantDraft(draft) || quotaAmount === null)
    return null;

  const selectedUserIds = [
    ...new Set(draft.selectedUserIds.map((id) => id.trim()).filter(Boolean)),
  ].sort();
  const base = { quotaKind: draft.quotaKind, quotaAmount };

  if (draft.scope === "all_users") {
    return {
      ...base,
      scope: draft.scope,
      selectedUserIds: [],
      includeFuture: draft.includeFuture,
    };
  }

  if (draft.scope === "selected_users") {
    return {
      ...base,
      scope: draft.scope,
      selectedUserIds,
      includeFuture: false,
    };
  }

  const input: AutoDormCheckGrantInput = {
    ...base,
    scope: draft.scope,
    selectedUserIds: [],
    includeFuture: draft.includeFuture,
  };
  if (draft.scope === "paid_plan" && draft.paidPlanCode) {
    input.paidPlanCode = draft.paidPlanCode;
  }
  return input;
}

export function autoDormCheckGrantFingerprint(
  input: AutoDormCheckGrantInput,
): string {
  return JSON.stringify(input);
}
